import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { authController } from '@/states/authStateController';
import { useAuth } from '@chainlit/react-client';
import {
  BASE_URL,
  PAYMENT_AMOUNT_ENDPOINT,
  SUBSCRIPTION_STATUS_ENDPOINT,
} from '@/lib/constants';

export default function PaymentPage() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const [paymentAmount, setPaymentAmount] = useState<number>(14.99);
  const [currencySymbol, setCurrencySymbol] = useState<string>('$');
  const navigate = useNavigate();
  const { logout: chainlitLogout } = useAuth();

  const { user, isLoading } = authController.useState(['user', 'isLoading']);

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user?.id) return;

      try {
        const response = await fetch(
          `${BASE_URL}${SUBSCRIPTION_STATUS_ENDPOINT}?user_id=${user.id}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Update user subscription status in auth controller
          if (data.is_subscribed !== user.is_subscribed) {
            authController.updateSubscriptionStatus({
              is_subscribed: data.is_subscribed,
              subscription_id: data.subscription_id,
              subscription_expiry_date: data.subscription_expiry_date
                ? new Date(data.subscription_expiry_date).toISOString()
                : null,
            });
          }
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }
    };

    checkSubscriptionStatus();
  }, [user?.id]);

  // Fetch payment amount from API
  useEffect(() => {
    const fetchPaymentAmount = async () => {
      try {
        const response = await fetch(`${BASE_URL}${PAYMENT_AMOUNT_ENDPOINT}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentAmount(data.amount);
          if (data.currency_symbol) setCurrencySymbol(data.currency_symbol);
        } else {
          console.error('Failed to fetch payment amount, using default');
        }
      } catch (error) {
        console.error('Error fetching payment amount:', error);
      }
    };

    fetchPaymentAmount();
  }, []);

  const handleStripePayment = async () => {
    try {
      setIsProcessing(true);
      setError('');

      if (!user) {
        setError('User not found');
        return;
      }

      // Call API to create Stripe checkout session
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL
        }/api/v1/payment/create-checkout-session?user_id=${user.id}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create payment session');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogout = () => {
    chainlitLogout(true);
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if no user is logged in (only after loading is complete)
  if (!user) {
    navigate('/login');
    return null;
  }

  const hasActiveSubscription = user.is_subscribed;
  const hasValidExpiryDate = user.subscription_expiry_date
    ? new Date(user.subscription_expiry_date) > new Date()
    : false;

  // Show success state if subscription is already active
  if (hasActiveSubscription || hasValidExpiryDate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <Logo className="h-8" />
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-slate-50 hover:to-slate-100 hover:border-slate-400 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-slate-900 font-medium"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Success Content */}
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-green-400 rounded-full blur-xl opacity-30 animate-pulse"></div>
                <CheckCircle
                  className="relative mx-auto h-20 w-20 text-green-500"
                  strokeWidth={2.5}
                />
              </div>
              <h2 className="mt-6 text-3xl font-extrabold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Subscription Active!
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Your subscription is active and you have full access.
              </p>
            </div>

            <Card className="border-2 border-green-100 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <p className="text-sm text-slate-600 font-medium">
                    You now have full access to the Sales Rep Chatbot.
                  </p>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                    size="lg"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show payment form for unverified users
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo className="h-8" />

            <div className="flex gap-2">
              {hasValidExpiryDate && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-blue-700 font-medium"
                >
                  Go to App
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-slate-50 hover:to-slate-100 hover:border-slate-400 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-slate-900 font-medium"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-blue-400 rounded-full blur-xl opacity-30"></div>
              <CreditCard
                className="relative mx-auto h-14 w-14 text-blue-600"
                strokeWidth={2}
              />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Subscribe to Access
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Subscribe to get unlimited access to the Sales Rep Chatbot
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <XCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <Card className="border-2 border-slate-200 shadow-xl bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-shadow duration-300">
            <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-xl text-slate-900">
                Sales Rep Chatbot Subscription
              </CardTitle>
              <CardDescription className="text-slate-600">
                Get unlimited monthly access to our AI-powered sales coaching platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <span className="text-sm font-semibold text-slate-700">
                    Monthly Subscription
                  </span>
                  <div className="text-right">
                    <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      {currencySymbol}{paymentAmount.toFixed(2)}
                    </span>
                    <span className="text-sm text-slate-500 font-medium">/mo</span>
                  </div>
                </div>
                <div className="space-y-2.5 pt-2">
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <p className="text-sm text-slate-700">
                      Unlimited AI coaching sessions
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <p className="text-sm text-slate-700">
                      Personalized sales strategies
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <p className="text-sm text-slate-700">
                      Real-time performance insights
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <p className="text-sm text-slate-700">
                      24/7 access to training materials
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <p className="text-sm text-slate-700">
                      Monthly billing, cancel anytime
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleStripePayment}
                disabled={isProcessing}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
                size="lg"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscribe with Stripe
                  </>
                )}
              </Button>

              <div className="text-center">
                <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                  <span className="text-green-500">🔒</span>
                  Secure payment powered by Stripe
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <p className="text-sm text-slate-600">
              Need help? Contact our support team at{' '}
              <a
                href="mailto:support@salesrep.com"
                className="text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2 transition-colors"
              >
                support@salesrep.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
